import { Bookmark, ChevronDown, Save, SlidersHorizontal, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import {
  useDeleteWeightPreset,
  useSaveWeightPreset,
  useWeightPresets,
} from '@/api/hooks'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { Input, RangeField } from '@/components/ui/Field'
import { useToast } from '@/components/ui/toastContext'
import {
  DEFAULT_TUNING,
  TUNING_FIELDS,
  activeTuningCount,
  sameTuning,
  tuningFromWeights,
  type Tuning,
  type TuningKey,
} from './tuning'

/**
 * The weight panel hides behind a toggle so the recommendations lead the screen,
 * the same way the recipe list's filters do. The page controls which tuning
 * panel is open, and starts this one expanded when the URL already carries
 * weights so a tuned bookmark never looks like it ignored them.
 */
export function WeightPanel({
  tuning,
  onChange,
  open,
  onOpenChange,
  className,
}: {
  tuning: Tuning
  onChange: (next: Tuning) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  className?: string
}) {
  const container = useRef<HTMLDivElement>(null)
  // A slider fires on every step of a drag. The draft moves the thumb while
  // dragging and the URL is only written on release, so one drag is one request.
  const [draft, setDraft] = useState(tuning)
  // Committing, resetting or stepping back through history all change the
  // weights without the sliders being touched. Adopt them during render rather
  // than in an effect, which would leave the stale position on screen for a
  // frame. Comparing by value keeps this safe whatever the caller hands over.
  const [adopted, setAdopted] = useState(tuning)
  if (!sameTuning(adopted, tuning)) {
    setAdopted(tuning)
    setDraft(tuning)
  }

  // A click outside the panel closes it. Bound on the container rather than the
  // panel so the toggle button itself is inside the ref.
  useEffect(() => {
    if (!open) return
    const onClick = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) onOpenChange(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open, onOpenChange])

  const fields = TUNING_FIELDS
  const changed = activeTuningCount(tuning)

  function stage(key: TuningKey, value: number) {
    setDraft((current) => ({ ...current, [key]: value }))
  }

  return (
    <div ref={container} className={cn(className)}>
      <div className="col-start-2 row-start-1 flex flex-wrap items-center gap-2">
        <Button
          variant={changed > 0 ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => onOpenChange(!open)}
          aria-expanded={open}
          aria-controls="recommendation-weights"
        >
          <SlidersHorizontal size={14} aria-hidden />
          Weights
          {changed > 0 ? (
            <span className="tabular ml-0.5 rounded-full bg-accent px-1.5 py-px text-micro text-on-accent">
              {changed}
            </span>
          ) : null}
        </Button>
      </div>

      {open ? (
        <div
          id="recommendation-weights"
          className="col-span-3 row-start-2 space-y-4 rounded-[var(--radius-card)] border border-line bg-surface p-4"
        >
          {/* Commits on release rather than on every step, for a mouse drag and
              for keyboard arrows alike. */}
          <div
            className="grid gap-4 sm:grid-cols-2"
            onPointerUp={() => onChange(draft)}
            onKeyUp={() => onChange(draft)}
          >
            {fields.map((field) => (
              <RangeField
                key={field.key}
                label={field.label}
                hint={field.hint}
                value={draft[field.key]}
                min={field.min}
                max={field.max}
                step={field.step}
                format={field.format}
                onValueChange={(value) => stage(field.key, value)}
              />
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <PresetControls weights={tuning} onLoad={onChange} />
            {changed > 0 ? (
              <Button
                variant="ghost"
                size="sm"
                aria-label="Reset weights to defaults"
                onClick={() => onChange(DEFAULT_TUNING)}
              >
                Reset weights
              </Button>
            ) : null}
          </div>
          <p className="text-micro text-pretty text-ink-faint">
            Rankings are re-run when you let go of a slider. The defaults
            reproduce the original scoring exactly.
          </p>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Saved weight sets, shared by the whole recommendation workflow.
 *
 * A row per set with its own remove button, matching the rule presets. Loading
 * one goes through `onChange`, so it lands in the URL and is remembered like
 * any other weight change.
 */
function PresetControls({
  weights,
  onLoad,
}: {
  weights: Tuning
  onLoad: (weights: Tuning) => void
}) {
  const presets = useWeightPresets()
  const save = useSaveWeightPreset()
  const remove = useDeleteWeightPreset()
  const toast = useToast()
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const saved = presets.data ?? []

  async function forget(presetId: number, presetName: string) {
    setBusy(true)
    try {
      await remove.mutateAsync(presetId)
      toast.success(`Removed “${presetName}”.`)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Could not remove that weight set.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <div ref={container} className="relative">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-controls="weight-presets"
        >
          <Bookmark size={13} aria-hidden />
          Saved weight sets
          {saved.length > 0 ? (
            <span className="tabular ml-0.5 rounded-full bg-accent px-1.5 py-px text-micro text-on-accent">
              {saved.length}
            </span>
          ) : null}
          <ChevronDown size={13} aria-hidden />
        </Button>

        {open ? (
          <div
            id="weight-presets"
            className="absolute top-full left-0 z-20 mt-1 w-64 max-w-[calc(100vw-2rem)] rounded-[var(--radius-field)] border border-line bg-surface p-1 shadow-raised sm:right-0 sm:left-auto"
          >
            {saved.length === 0 ? (
              <p className="px-2 py-3 text-center text-xs text-ink-faint">
                No weight sets saved yet.
              </p>
            ) : (
              <ul className="space-y-0.5">
                {saved.map((preset) => {
                  const loaded = tuningFromWeights(preset.weights)
                  const changed = activeTuningCount(loaded)
                  return (
                    <li key={preset.id} className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          onLoad(loaded)
                          setOpen(false)
                        }}
                        className="min-w-0 flex-1 rounded-md px-2 py-1.5 text-left text-sm text-ink hover:bg-surface-sunken"
                      >
                        <span className="block truncate">{preset.name}</span>
                        <span className="block text-micro text-ink-faint">
                          {changed === 0
                            ? 'Defaults'
                            : `${changed} tuned ${changed === 1 ? 'weight' : 'weights'}`}
                        </span>
                      </button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove “${preset.name}”`}
                        title={`Remove “${preset.name}”`}
                        onClick={() => void forget(preset.id, preset.name)}
                        disabled={busy}
                      >
                        <Trash2 size={12} aria-hidden />
                      </Button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        ) : null}
      </div>

      <span className="flex items-center">
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Save as…"
          aria-label="Name for this weight set"
          wrapperClassName="w-36"
          className="h-8 py-1 text-micro"
        />
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Save these weights"
          disabled={busy || name.trim() === ''}
          onClick={async () => {
            const trimmed = name.trim()
            if (!trimmed) return
            setBusy(true)
            try {
              await save.mutateAsync({ name: trimmed, weights })
              setName('')
              toast.success(`Saved “${trimmed}”.`)
            } catch (error) {
              toast.error(
                error instanceof Error ? error.message : 'Could not save that.',
              )
            } finally {
              setBusy(false)
            }
          }}
        >
          <Save size={13} aria-hidden />
        </Button>
      </span>
    </div>
  )
}